import React from 'react';
import { MessageSquare, Send, Clock, CheckCircle, XCircle, AlertCircle, Filter, Trash2 } from 'lucide-react';
import { collection, query, orderBy, updateDoc, doc, addDoc, Timestamp, deleteDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { useCollection } from '../hooks/useCollection';
import ConfirmDialog from './ConfirmDialog';

interface SupportTicket {
  id: string;
  userId: string;
  userEmail: string;
  subject: string;
  message: string;
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  createdAt: Date;
  updatedAt: Date;
  responses: {
    id: string;
    userId: string;
    userEmail: string;
    message: string;
    createdAt: Date;
    isAdmin: boolean;
  }[];
}

export default function AdminSupport() {
  const { user } = useAuth();
  const [selectedTicket, setSelectedTicket] = React.useState<SupportTicket | null>(null);
  const [reply, setReply] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<string>('all');
  const [loading, setLoading] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [ticketToDelete, setTicketToDelete] = React.useState<SupportTicket | null>(null);

  const { data: tickets } = useCollection<SupportTicket>(
    query(
      collection(db, 'supportTickets'),
      orderBy('createdAt', 'desc')
    )
  );

  const filteredTickets = React.useMemo(() => {
    if (statusFilter === 'all') return tickets;
    return tickets?.filter((ticket) => ticket.status === statusFilter);
  }, [tickets, statusFilter]);

  const handleStatusChange = async (ticketId: string, newStatus: string) => {
    if (!user) return;

    setLoading(true);
    try {
      const ticketRef = doc(db, 'supportTickets', ticketId);
      await updateDoc(ticketRef, {
        status: newStatus,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating ticket status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !selectedTicket) return;

    setLoading(true);
    try {
      const response = {
        id: crypto.randomUUID(),
        userId: user.uid,
        userEmail: user.email,
        message: reply,
        createdAt: Timestamp.now(),
        isAdmin: true
      };

      const ticketRef = doc(db, 'supportTickets', selectedTicket.id);
      await updateDoc(ticketRef, {
        responses: [...selectedTicket.responses, response],
        status: 'in-progress',
        updatedAt: Timestamp.now()
      });

      setReply('');
    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (ticket: SupportTicket) => {
    setTicketToDelete(ticket);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!ticketToDelete) return;

    try {
      await deleteDoc(doc(db, 'supportTickets', ticketToDelete.id));
      if (selectedTicket?.id === ticketToDelete.id) {
        setSelectedTicket(null);
      }
    } catch (error) {
      console.error('Error deleting ticket:', error);
    } finally {
      setTicketToDelete(null);
      setShowDeleteConfirm(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'in-progress':
        return <AlertCircle className="w-5 h-5 text-blue-500" />;
      case 'resolved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'closed':
        return <XCircle className="w-5 h-5 text-gray-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
            <MessageSquare className="w-5 h-5 mr-2" />
            Support Tickets
          </h3>
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Tickets</option>
              <option value="open">Open</option>
              <option value="in-progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="space-y-4">
          {filteredTickets?.map((ticket) => (
            <div
              key={ticket.id}
              className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200"
            >
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">{ticket.subject}</h4>
                    <p className="text-sm text-gray-500">From: {ticket.userEmail}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <select
                      value={ticket.status}
                      onChange={(e) => handleStatusChange(ticket.id, e.target.value)}
                      className="rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="open">Open</option>
                      <option value="in-progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                    <button
                      onClick={() => setSelectedTicket(ticket)}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => handleDeleteClick(ticket)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <p className="text-gray-600">{ticket.message}</p>
                <div className="mt-2 text-sm text-gray-500">
                  Created: {ticket.createdAt.toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}

          {(!filteredTickets || filteredTickets.length === 0) && (
            <div className="text-center py-12">
              <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No tickets found</h3>
              <p className="mt-1 text-sm text-gray-500">
                No support tickets match your current filter.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{selectedTicket.subject}</h3>
                <p className="text-sm text-gray-500">From: {selectedTicket.userEmail}</p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleDeleteClick(selectedTicket)}
                  className="text-red-600 hover:text-red-900 p-2"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setSelectedTicket(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">{selectedTicket.message}</p>
                <p className="mt-2 text-sm text-gray-500">
                  {selectedTicket.createdAt.toLocaleDateString()}
                </p>
              </div>

              {selectedTicket.responses.map((response) => (
                <div
                  key={response.id}
                  className={`p-4 rounded-lg ${
                    response.isAdmin ? 'bg-blue-50 ml-4' : 'bg-gray-50 mr-4'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm font-medium text-gray-900">
                      {response.isAdmin ? 'Support Team' : response.userEmail}
                    </span>
                    <span className="text-xs text-gray-500">
                      {response.createdAt.toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-gray-700">{response.message}</p>
                </div>
              ))}

              <form onSubmit={handleReply} className="mt-4">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Type your reply..."
                  rows={3}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
                <div className="mt-3 flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Reply
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && ticketToDelete && (
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setTicketToDelete(null);
          }}
          onConfirm={handleConfirmDelete}
          title="Delete Support Ticket"
          message={`Are you sure you want to delete this support ticket from ${ticketToDelete.userEmail}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="warning"
        />
      )}
    </div>
  );
}